import React from 'react'
import {
  Card,
  CardContent,
  CardActions,
  Button,
  Switch,
  FormControlLabel,
  Typography,
  Tooltip
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faEdit,
  faTrash,
  faPlay,
  faUsers,
  faChartLine,
  faCalendarDay,
  faCalendarWeek,
  faClipboardCheck,
  faPhoneSlash,
  faEnvelope,
  faBell
} from '@fortawesome/free-solid-svg-icons'
import PropTypes from 'prop-types'
import styles from './AutomationCard.module.scss'

const TYPE_CONFIG = {
  plan_usage: { label: 'Plan Usage Alert', icon: faChartLine, iconClass: 'planUsage' },
  daily_summary: { label: 'Daily Summary', icon: faCalendarDay, iconClass: 'dailySummary' },
  weekly_report: { label: 'Weekly Report', icon: faCalendarWeek, iconClass: 'weeklyReport' },
  qa_alert: { label: 'QA Alert', icon: faClipboardCheck, iconClass: 'qaAlert' },
  missed_call: { label: 'Missed Call', icon: faPhoneSlash, iconClass: 'missedCall' }
}

const AutomationCard = ({
  automation,
  onEdit,
  onDelete,
  onToggle,
  onTest,
  onManageCampaigns,
  className
}) => {
  const typeConfig = TYPE_CONFIG[automation.type] || {
    label: automation.type ? automation.type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : automation.type,
    icon: faBell,
    iconClass: 'default'
  }

  const getConfigSummary = (config, type) => {
    if (config?.trigger?.threshold) {
      return `Triggers at ${config.trigger.threshold}% usage`
    }
    if (config?.schedule) {
      return `Schedule: ${config.schedule}`
    }
    switch (type) {
      case 'daily_summary': return 'Runs daily'
      case 'weekly_report': return 'Runs weekly'
      case 'qa_alert': return 'Triggers on low QA scores'
      case 'missed_call': return 'Triggers on missed calls'
      default: return 'Automatic trigger'
    }
  }

  const cardClass = `${styles.card} ${automation.enabled ? styles.enabled : styles.disabled} ${className || ''}`

  return (
    <Card className={cardClass}>
      <CardContent>
        <div className={styles.header}>
          <Typography variant='h6' className={styles.title}>
            {automation.name}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={automation.enabled}
                onChange={(e) => onToggle(automation.id, e.target.checked)}
                color='primary'
              />
            }
            label='Enabled'
            labelPlacement='start'
          />
        </div>

        <div className={styles.typeRow}>
          <div className={`${styles.typeIcon} ${styles[typeConfig.iconClass]}`}>
            <FontAwesomeIcon icon={typeConfig.icon} />
          </div>
          <span className={styles.typeLabel}>{typeConfig.label}</span>
        </div>

        <Typography variant='body2' className={styles.configSummary}>
          {getConfigSummary(automation.config, automation.type)}
        </Typography>

        <div className={styles.templateRow}>
          <FontAwesomeIcon icon={faEnvelope} className={styles.templateIcon} />
          <span>{automation.template_name || 'Default template'}</span>
        </div>
      </CardContent>

      <CardActions className={styles.actions}>
        <Tooltip title='Edit Automation'>
          <span>
            <Button
              size='small'
              startIcon={<FontAwesomeIcon icon={faEdit} />}
              onClick={() => onEdit(automation)}
              disabled={!automation.enabled}
            >
              Edit
            </Button>
          </span>
        </Tooltip>

        <Tooltip title='Manage Campaign Subscriptions'>
          <Button
            size='small'
            startIcon={<FontAwesomeIcon icon={faUsers} />}
            onClick={() => onManageCampaigns(automation)}
          >
            Campaigns
          </Button>
        </Tooltip>

        <Tooltip title='Test Automation'>
          <span>
            <Button
              size='small'
              startIcon={<FontAwesomeIcon icon={faPlay} />}
              onClick={() => onTest(automation)}
              disabled={!automation.enabled}
            >
              Test
            </Button>
          </span>
        </Tooltip>

        <Tooltip title='Delete Automation'>
          <Button
            size='small'
            startIcon={<FontAwesomeIcon icon={faTrash} />}
            onClick={() => onDelete(automation.id)}
            className={styles.deleteBtn}
          >
            Delete
          </Button>
        </Tooltip>
      </CardActions>
    </Card>
  )
}

AutomationCard.propTypes = {
  automation: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  onTest: PropTypes.func.isRequired,
  onManageCampaigns: PropTypes.func.isRequired,
  className: PropTypes.string
}

export default AutomationCard
